import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import api from "../lib/api";
import { toast } from "sonner";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import SEO from "../components/SEO";
import { Buildings, CheckCircle, X, Clock, Prohibit, DownloadSimple, Warning, Envelope, Phone, MapTrifold, Tag } from "@phosphor-icons/react";

const STATUS_TABS = [
  { key: "pending_approval", label: "Pending", icon: Clock, color: "amber" },
  { key: "approved", label: "Approved", icon: CheckCircle, color: "emerald" },
  { key: "rejected", label: "Rejected", icon: X, color: "red" },
  { key: "suspended", label: "Suspended", icon: Prohibit, color: "slate" },
];

const AdminDealers = ({ user, logout }) => {
  const [activeStatus, setActiveStatus] = useState("pending_approval");
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const canManage = ["admin", "manager"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/dealers`, { params: { status: activeStatus } });
      setDealers(r.data || []);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, [activeStatus]);

  useEffect(() => { if (canManage) load(); }, [load, canManage]);

  const approve = async (d) => {
    if (!window.confirm(`Approve ${d.name} (${d.dealer_profile?.company_name})?`)) return;
    try {
      await api.post(`/dealers/${d.user_id}/approve`, { approver_name: user.name });
      toast.success(`${d.name} approved — email notification sent`);
      setDetail(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const reject = async (d) => {
    const reason = window.prompt("Rejection reason (shown to dealer):") || "";
    if (reason === null) return;
    try {
      await api.post(`/dealers/${d.user_id}/reject`, { reason, approver_name: user.name });
      toast.success("Rejected"); setDetail(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const suspend = async (d) => {
    if (!window.confirm(`Suspend ${d.name}? They won't be able to login.`)) return;
    try {
      await api.post(`/dealers/${d.user_id}/suspend`);
      toast.success("Suspended"); setDetail(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (!canManage) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar user={user} logout={logout} />
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center"><Warning size={40} className="mx-auto text-amber-500 mb-3" />
            <h1 className="text-xl font-bold mb-1">Admin access required</h1></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <SEO title="Dealer Management — SwifTech India" description="Manage dealer applications and approvals" />
      <div className="flex-1">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="mb-6">
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-1">Admin</div>
            <h1 className="text-3xl font-bold flex items-center gap-3" style={{fontFamily: 'Outfit, sans-serif'}}>
              <Buildings size={28} weight="fill" className="text-[#002FA7]" /> Dealer Management
            </h1>
            <p className="text-sm text-slate-600 mt-1">Review applications, approve dealers, and manage the dealer network.</p>
          </div>

          {/* Status tabs */}
          <div className="flex gap-1 border-b border-slate-200 mb-6 flex-wrap">
            {STATUS_TABS.map(t => {
              const I = t.icon;
              return (
                <button key={t.key} onClick={() => setActiveStatus(t.key)} data-testid={`dealers-tab-${t.key}`}
                  className={`px-5 py-2.5 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition-all ${activeStatus === t.key ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                  <I size={14} weight={activeStatus === t.key ? "fill" : "regular"} /> {t.label}
                </button>
              );
            })}
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/dealers/price-list.pdf?requester_role=${user.role}`} target="_blank" rel="noopener noreferrer"
              className="ml-auto text-sm text-[#002FA7] hover:underline flex items-center gap-1 pb-2">
              <DownloadSimple size={14} /> Preview Dealer Price List PDF
            </a>
          </div>

          {loading ? (
            <div className="py-10 flex justify-center"><div className="loading-spinner" /></div>
          ) : dealers.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded py-16 text-center">
              <Buildings size={40} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">No {STATUS_TABS.find(t => t.key === activeStatus)?.label.toLowerCase()} dealers.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {dealers.map(d => <DealerCard key={d.user_id} dealer={d} onClick={() => setDetail(d)} />)}
            </div>
          )}
        </div>
      </div>

      {detail && <DealerDetailModal dealer={detail} onClose={() => setDetail(null)}
        onApprove={() => approve(detail)} onReject={() => reject(detail)} onSuspend={() => suspend(detail)} />}
    </div>
  );
};

const DealerCard = ({ dealer, onClick }) => {
  const p = dealer.dealer_profile || {};
  return (
    <div onClick={onClick} data-testid={`dealer-card-${dealer.user_id}`}
      className="bg-white border border-slate-200 hover:border-[#002FA7] hover:shadow-md p-4 cursor-pointer transition-all">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <h3 className="font-bold text-base truncate" style={{fontFamily: 'Outfit, sans-serif'}}>{p.company_name || dealer.name}</h3>
          <p className="text-xs text-slate-500 truncate">{dealer.name}</p>
        </div>
        {p.company_gstin && <span className="text-[10px] bg-emerald-50 text-emerald-800 font-mono px-1.5 py-0.5 font-bold border border-emerald-200">GST</span>}
      </div>
      <div className="text-xs text-slate-600 space-y-1">
        <div className="flex items-center gap-1.5"><Envelope size={12} /> {dealer.email}</div>
        {p.phone && <div className="flex items-center gap-1.5"><Phone size={12} /> {p.phone}</div>}
        {p.territory && <div className="flex items-center gap-1.5"><MapTrifold size={12} /> {p.territory}</div>}
        {p.business_type && <div className="flex items-center gap-1.5"><Tag size={12} /> <span className="capitalize">{p.business_type.replace(/_/g, ' ')}</span></div>}
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-500">
        Applied {p.applied_at ? new Date(p.applied_at).toLocaleDateString('en-IN') : "—"}
      </div>
    </div>
  );
};

const DealerDetailModal = ({ dealer, onClose, onApprove, onReject, onSuspend }) => {
  const p = dealer.dealer_profile || {};
  const status = dealer.dealer_status || "pending_approval";
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white shadow-2xl max-w-2xl w-full my-4" onClick={e => e.stopPropagation()} data-testid="dealer-detail-modal">
        <div className="border-b border-slate-200 px-6 py-4 flex justify-between items-center">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Dealer application</div>
            <h2 className="text-xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{p.company_name || dealer.name}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          <InfoBlock title="Primary Contact">
            <InfoRow label="Name" value={dealer.name} />
            <InfoRow label="Email" value={dealer.email} />
            <InfoRow label="Phone" value={p.phone} />
          </InfoBlock>
          <InfoBlock title="Company">
            <InfoRow label="Company Name" value={p.company_name} />
            <InfoRow label="GSTIN" value={p.company_gstin || "—"} mono />
            <InfoRow label="Territory" value={p.territory || "—"} />
            <InfoRow label="Address" value={p.company_address || "—"} />
          </InfoBlock>
          <InfoBlock title="Business Profile">
            <InfoRow label="Business Type" value={p.business_type?.replace(/_/g, ' ') || "—"} />
            <InfoRow label="Annual Turnover" value={p.annual_turnover || "—"} />
            <InfoRow label="Applied On" value={p.applied_at ? new Date(p.applied_at).toLocaleString('en-IN') : "—"} />
            {p.references && <InfoRow label="References" value={p.references} />}
          </InfoBlock>
          {status === "rejected" && p.rejection_reason && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm">
              <div className="text-[11px] uppercase tracking-wider text-red-700 font-bold mb-1">Rejection Reason</div>
              <p>{p.rejection_reason}</p>
            </div>
          )}
          {status === "approved" && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
              <CheckCircle size={16} weight="fill" className="text-emerald-600 inline mr-1" />
              Approved by {p.approved_by || "—"} on {p.approved_at ? new Date(p.approved_at).toLocaleString('en-IN') : "—"}
            </div>
          )}
        </div>
        <div className="border-t border-slate-200 px-6 py-4 flex justify-end gap-2 flex-wrap">
          <button onClick={onClose} className="border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50">Close</button>
          {status === "pending_approval" && (
            <>
              <button onClick={onReject} className="border border-red-300 text-red-700 hover:bg-red-50 px-4 py-2 text-sm font-bold" data-testid="dealer-reject-btn">Reject</button>
              <button onClick={onApprove} className="bg-emerald-600 text-white hover:bg-emerald-700 px-5 py-2 text-sm font-bold flex items-center gap-1.5" data-testid="dealer-approve-btn">
                <CheckCircle size={14} weight="bold" /> Approve Dealer
              </button>
            </>
          )}
          {status === "approved" && (
            <button onClick={onSuspend} className="border border-amber-400 text-amber-700 hover:bg-amber-50 px-4 py-2 text-sm font-bold" data-testid="dealer-suspend-btn">Suspend</button>
          )}
          {status === "rejected" && (
            <button onClick={onApprove} className="bg-emerald-600 text-white hover:bg-emerald-700 px-4 py-2 text-sm font-bold">Approve Anyway</button>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoBlock = ({ title, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2 pb-1 border-b border-slate-200">{title}</div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">{children}</div>
  </div>
);
const InfoRow = ({ label, value, mono }) => (
  <div className="text-sm">
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`font-medium ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
  </div>
);

export default AdminDealers;
